import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import { API_URL } from "../../../../api";

export default function DiscountUpdate() {
  const navigate = useNavigate();
  const discountId = useLocation().pathname.split("/")[2];
  const token = localStorage.getItem("token");
  const url = API_URL();
  const api = axios.create({
    baseURL: url,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState("");
  const [discountRate, setDiscountRate] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [expiredDay, setExpiredDay] = useState(0);
  const [itemBase, setItemBase] = useState(0);
  const [certainAmount, setCertainAmount] = useState(0);

  const [treatLists, setTreatLists] = useState([]);
  const [unitLists, setUnitLists] = useState([]);
  const [itemID, setItemID] = useState("");
  const [chooseUnitList, setChooseUnitList] = useState(null);
  const [itemUnitOptions, setItemUnitOptions] = useState([]);
  const [selectedItemUnits, setSelectedItemUnits] = useState([]);
  const [focProductId, setFocProductId] = useState("");
  const [focItemLists, setFocItemLists] = useState([]);
  const [focItemId, setFocItemId] = useState("");
  const [focUnitOptions, setFocUnitOptions] = useState([]);
  const [focUnitId, setFocUnitId] = useState(null);
  const isInitialLoad = useRef(true);

  const fetchUnits = async (productId) => {
    if (!productId) {
      setUnitLists([]);
      setChooseUnitList(null);
      setItemUnitOptions([]);
      setSelectedItemUnits([]);
      return;
    }
    try {
      const res = await api.get(`medicine-items?relatedProduct=${productId}`);
      setUnitLists(res.data.list || []);
    } catch (err) {}
  };

  const fetchItemUnits = async (itemId) => {
    if (!itemId) {
      setItemUnitOptions([]);
      setSelectedItemUnits([]);
      return;
    }
    if (discountType === "quantityBased") {
      try {
        const res = await api.get("item-unit/options", {
          params: { relatedItem: itemId },
        });
        setItemUnitOptions(res.data.data || []);
      } catch (err) {}
    }
  };

  // Fetch FOC items when product changes
  useEffect(() => {
    if (!focProductId) {
      setFocItemLists([]);
      setFocItemId("");
      return;
    }
    const fetch = async () => {
      try {
        const res = await api.get(
          `medicine-items?relatedProduct=${focProductId}`,
        );
        setFocItemLists(res.data.list || []);
      } catch (err) {}
    };
    fetch();
  }, [focProductId]);

  // Fetch FOC units when item changes
  useEffect(() => {
    if (!focItemId || discountRate !== "FOC") {
      setFocUnitOptions([]);
      setFocUnitId(null);
      return;
    }
    const fetch = async () => {
      try {
        const res = await api.get("item-unit/options", {
          params: { relatedItem: focItemId },
        });
        setFocUnitOptions(res.data.data || []);
      } catch (err) {}
    };
    fetch();
  }, [focItemId, discountRate]);

  // Clear selections when discount type changes (after initial load)
  useEffect(() => {
    if (isInitialLoad.current) return;
    setItemUnitOptions([]);
    setSelectedItemUnits([]);
  }, [discountType]);

  // Load existing discount data
  useEffect(() => {
    const getDiscount = async () => {
      try {
        const res = await api.get(`item-discounts/${discountId}`);
        const d = res.data.data;
        const discountTypeVal = d.discountType || "";
        setName(d.discountName || "");
        setDescription(d.description || "");
        setDiscountType(discountTypeVal);
        setDiscountRate(d.discountRate || "");
        setDiscountValue(d.discountValue || "");
        setExpiredDay(d.expiredDay || 0);
        setItemBase(d.itemBase || 0);
        setCertainAmount(d.certainAmount || 0);

        const savedItem = Array.isArray(d.relatedItem)
          ? d.relatedItem[0]
          : d.relatedItem;

        const productId = savedItem?.name?.toString();

        if (productId) {
          setItemID(productId);
          const unitRes = await api.get(
            `medicine-items?relatedProduct=${productId}`,
          );
          const units = unitRes.data.list || [];
          setUnitLists(units);

          setChooseUnitList(savedItem);

          if (discountTypeVal === "quantityBased") {
            const iuRes = await api.get("item-unit/options", {
              params: { relatedItem: savedItem._id },
            });
            const itemUnits = iuRes.data.data || [];
            setItemUnitOptions(itemUnits);

            const savedIuIds = (d.relatedItemUnit || []).map((id) =>
              id?.toString(),
            );
            const selected = itemUnits
              .filter((u) => savedIuIds.includes(u._id?.toString()))
              .map((u) => ({ value: u._id, label: u.name }));
            setSelectedItemUnits(selected);
          }
        }

        // Load FOC data
        if (d.discountRate === "FOC") {
          const savedFocItem = Array.isArray(d.focItem)
            ? d.focItem[0]
            : d.focItem;
          const savedFocUnit = Array.isArray(d.focUnit)
            ? d.focUnit[0]
            : d.focUnit;

          if (savedFocItem) {
            const focItemProductId = savedFocItem?.name?.toString();
            if (focItemProductId) {
              setFocProductId(focItemProductId);
              const focItemRes = await api.get(
                `medicine-items?relatedProduct=${focItemProductId}`,
              );
              setFocItemLists(focItemRes.data.list || []);
            }
            setFocItemId(
              savedFocItem._id?.toString() || savedFocItem.toString(),
            );
          }

          if (savedFocUnit) {
            const focUnitRes = await api.get("item-unit/options");
            const allUnits = focUnitRes.data.data || [];
            setFocUnitOptions(allUnits);
            setFocUnitId(
              savedFocUnit._id?.toString() || savedFocUnit.toString(),
            );
          }
        }
      } catch (err) {
        Swal.fire({
          title: "Error",
          text: "Failed to load discount",
          icon: "error",
        });
      }
    };
    const getTreatmentList = async () => {
      try {
        const res = await api.get("medicine-lists");
        setTreatLists(res.data.list || []);
      } catch (err) {}
    };
    Promise.all([getDiscount(), getTreatmentList()]).finally(() => {
      isInitialLoad.current = false;
    });
  }, []);

  const handleUpdate = () => {
    const data = {
      discountName: name,
      discountRate,
      description,
      discountValue: parseInt(discountValue),
      discountType,
    };
    if (discountType === "Expired") {
      data.expiredDay = Number(expiredDay);
    } else if (discountType === "Voucher") {
      data.certainAmount = parseInt(certainAmount);
    } else if (discountType === "quantityBased") {
      data.relatedItemUnit = selectedItemUnits.map((i) => i.value);
      data.itemBase = Number(itemBase);
    }

    if (discountRate === "FOC") {
      data.discountValue = 0;
      data.focItem = focItemId ? [focItemId] : [];
      data.focUnit = focUnitId ? [focUnitId] : [];
    }

    api
      .put(`item-discounts/${discountId}`, data)
      .then(() => {
        Swal.fire({
          title: "Successful!",
          text: "Discount Updated!",
          icon: "success",
          showConfirmButton: false,
          timer: 2000,
        });
        navigate("/discount");
      })
      .catch((err) => {
        Swal.fire({
          title: "Something Wrong!",
          text: err.response?.data?.message || "Error",
          icon: "warning",
        });
      });
  };

  return (
    <div className="App">
      <div className="wrapper">
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-12">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/discount">Discount List</Link>
                    </li>
                    <li className="breadcrumb-item active">Update Discount</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-8 offset-md-2">
                  <div className="card">
                    <h5 className="card-header">Update Discount</h5>
                    <div className="card-body text-left">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          type="text"
                          className="form-control border border-info"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Discount Name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Discount Type</label>
                        <select
                          className="custom-select border-info"
                          value={discountType}
                          onChange={(e) => setDiscountType(e.target.value)}
                        >
                          <option value="">Choose</option>
                          <option value="Voucher">Voucher</option>
                          <option value="Expired">Expired Date</option>
                          <option value="quantityBased">Item Base</option>
                        </select>
                      </div>

                      {discountType === "Expired" && (
                        <div className="form-group">
                          <label>Expired Days</label>
                          <input
                            type="number"
                            className="form-control border border-info"
                            value={expiredDay}
                            onChange={(e) => setExpiredDay(e.target.value)}
                          />
                        </div>
                      )}
                      {discountType === "quantityBased" && (
                        <div className="form-group">
                          <label>Item Base Quantity</label>
                          <input
                            type="number"
                            className="form-control border border-info"
                            value={itemBase}
                            onChange={(e) => setItemBase(e.target.value)}
                          />
                        </div>
                      )}
                      {discountType === "Voucher" && (
                        <div className="form-group">
                          <label>Certain Amount</label>
                          <input
                            type="number"
                            className="form-control border border-info"
                            value={certainAmount}
                            onChange={(e) => setCertainAmount(e.target.value)}
                          />
                        </div>
                      )}

                      <div className="form-group">
                        <label>Discount Method</label>
                        <select
                          className="custom-select border-info"
                          value={discountRate}
                          onChange={(e) => setDiscountRate(e.target.value)}
                        >
                          <option value="">Choose Type</option>
                          <option value="Amount">Amount</option>
                          <option value="Percent">Percent</option>
                          <option value="FOC">FOC</option>
                        </select>
                      </div>

                      {discountRate === "FOC" ? (
                        <>
                          <div className="form-group">
                            <label>Free Product</label>
                            <select
                              className="custom-select border-info"
                              value={focProductId}
                              onChange={(e) => setFocProductId(e.target.value)}
                            >
                              <option value="">Choose</option>
                              {treatLists.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Free Item</label>
                            <select
                              className="custom-select border-info"
                              value={focItemId}
                              onChange={(e) => setFocItemId(e.target.value)}
                            >
                              <option value="">Choose</option>
                              {focItemLists
                                .filter(
                                  (el) =>
                                    !focProductId ||
                                    el.name?._id === focProductId,
                                )
                                .map((opt) => (
                                  <option key={opt._id} value={opt._id}>
                                    {opt.medicineItemName}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Free Unit</label>
                            <select
                              className="custom-select border-info"
                              value={focUnitId || ""}
                              onChange={(e) =>
                                setFocUnitId(e.target.value || null)
                              }
                            >
                              <option value="">Choose</option>
                              {focUnitOptions.map((u) => (
                                <option key={u._id} value={u._id}>
                                  {u.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      ) : (
                        <div className="form-group">
                          <label>Value</label>
                          <input
                            type="number"
                            className="form-control border border-info"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                          />
                        </div>
                      )}

                      {discountType !== "Voucher" && (
                        <>
                          <div className="form-group">
                            <label>Products</label>
                            <select
                              className="custom-select border-info"
                              value={itemID}
                              onChange={(e) => {
                                setItemID(e.target.value);
                                fetchUnits(e.target.value);
                              }}
                            >
                              <option value="">Choose</option>
                              {treatLists.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Items</label>
                            <select
                              className="form-control"
                              value={chooseUnitList?._id || ""}
                              onChange={(e) => {
                                const item = unitLists.find(
                                  (el) => el._id === e.target.value,
                                );
                                setChooseUnitList(item || null);
                                fetchItemUnits(e.target.value);
                              }}
                            >
                              <option value="">Choose</option>
                              {unitLists.map((opt) => (
                                <option key={opt._id} value={opt._id}>
                                  {opt.medicineItemName}
                                </option>
                              ))}
                            </select>
                          </div>

                          {discountType === "quantityBased" && (
                            <div className="form-group">
                              <label>Product Item Units</label>
                              <Select
                                isMulti
                                options={itemUnitOptions.map((u) => ({
                                  value: u._id,
                                  label: u.name,
                                }))}
                                value={selectedItemUnits}
                                onChange={setSelectedItemUnits}
                                closeMenuOnSelect={false}
                                placeholder="Choose item units"
                              />
                            </div>
                          )}
                        </>
                      )}

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          className="form-control"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>

                      <div className="form-group text-right">
                        <button
                          className="btn btn-secondary mr-2"
                          onClick={() => navigate("/discount")}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleUpdate}
                        >
                          Update
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
